import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import BlockUi from "react-block-ui";
import { withTranslation } from "react-i18next";
import Joyride from "react-joyride";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router";
import { useHistory } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import Http from "../../../api/Http";
import {
  getSubscriptionList,
} from "../../../redux/actions/calendarAction";
import { setIsServiceUser } from "../../../redux/actions/loginDetailsActions";
import { getLogoutData } from "../../../redux/actions/logoutActions";
import AuthService from "../../../session/AuthService";
import {
  getAllAreaOfInterest,
  getAllChatFriendsList,
  getAllCommonExpectationHelpList,
  getAllCommonExpectationInterestList,
  getAllCommonFriendsList,
  getAllCommonIndustry,
  getAllCommonProfessionList,
  getAllCommonSchoolList,
  getAllCommonTagsList,
  getApplicationRequirements,
} from "../../../redux/actions/commonApiActions";

import CalendarContainerV2 from "./CalendarContainerV2";
import { setDashFocusTab } from "../../../redux/actions/dashboardActions";
import "./ServiceUsersHome.css";
import MyYuukke from "../../../api/MyYuukke";

import {
  FiUser,
  FiUsers,
  FiClipboard,
  FiBarChart2,
  FiBriefcase,
  FiCalendar,
  FiMessageSquare,
  FiFileText,
  FiLogOut,
  FiHome,
  FiActivity,
  FiMoreHorizontal,
  FiBell,
} from "react-icons/fi";

// Maps a notification's `type` to the dashboard tab id it should navigate to.
// Adjust these ids/targets if a type should land somewhere else.
const NOTIFICATION_TYPE_TAB_MAP = {
  service_review: 13, // Feedback
  feedback_submitted: 13, // Feedback
  slot_booking: 4, // Calendar
};
const DEFAULT_NOTIFICATION_TAB = 7; // Home, used when a type isn't mapped

const ServiceUsersHome = (props) => {
  const [isToggleEnabled, setIsToggleEnabled] = useState(false);
  const [runTour, setRunTour] = useState(false);

  // State and Refs for Dropdowns
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [bellMenuOpen, setBellMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const moreMenuRef = useRef(null);
  const bellMenuRef = useRef(null);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifError, setNotifError] = useState(null);
  const [clearingNotifications, setClearingNotifications] = useState(false);

  const history = useHistory();

  const { userSlots, prefComplete, subscriptionStatus, expertBookings, userServiceList } = useSelector((state) => state.event);

  const steps = [
    true,
    prefComplete,
    subscriptionStatus,
    !!userSlots?.preference?.accountNumber,
  ];

  const completedSteps = steps.filter(Boolean).length;
  const profileCompletion = Math.round((completedSteps / steps.length) * 100);
  const isProfileIncomplete = profileCompletion < 100;

  const [state, setState] = useState({ loader: false });

  const dispatch = useDispatch();
  const focusedTb = useSelector(({ dashboard }) => dashboard.focusedTab);

  const ICON_SIZE = 15;
  const BOTTOM_NAV_ICON_SIZE = 20;

  const resTabList = [
    { title: "Home", id: 7, icon: <FiBarChart2 size={ICON_SIZE} /> },
    { title: "Activity", id: 10, icon: <FiClipboard size={ICON_SIZE} /> },
    { title: "Clients", id: 12, icon: <FiUsers size={ICON_SIZE} /> },
    { title: "Services & Resources", id: 5, icon: <FiBriefcase size={ICON_SIZE} /> },
    { title: "Calendar", id: 4, icon: <FiCalendar size={ICON_SIZE} /> },
    { title: "Feedback", id: 13, icon: <FiMessageSquare size={ICON_SIZE} /> },
    { title: "Pages", id: 8, icon: <FiFileText size={ICON_SIZE} /> },
  ];

  const BOTTOM_NAV_PRIMARY_IDS = [7, 10, 4, 8];
  const bottomNavPrimary = [
    { id: 7, label: "Home", icon: <FiHome size={BOTTOM_NAV_ICON_SIZE} /> },
    { id: 10, label: "Activity", icon: <FiActivity size={BOTTOM_NAV_ICON_SIZE} /> },
    { id: 4, label: "Calendar", icon: <FiCalendar size={BOTTOM_NAV_ICON_SIZE} /> },
    { id: 8, label: "Pages", icon: <FiFileText size={BOTTOM_NAV_ICON_SIZE} /> },
  ];
  const moreTabList = resTabList.filter((tb) => !BOTTOM_NAV_PRIMARY_IDS.includes(tb.id));
  const isMoreActive = moreTabList.some((tb) => tb.id === focusedTb);

  const logout = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { t } = props;

    Swal.fire({
      title: t("Are you sure?"),
      text: t("You want to logout?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("Yes, logout"),
      cancelButtonText: t("No, cancel!"),
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        AuthService.removeAuth();
        localStorage.removeItem("R_TOKEN");
        dispatch(getLogoutData());

        if (Http.isProfileExists("keycloak")) {
          AuthService.triggerLogout();
        } else {
          history.push("/login");
        }
      }
    });
  };

  const TOUR_STEPS = [
    {
      target: ".toggle-button",
      content: "Use this to switch to Yuukke community",
      disableBeacon: true,
    },
  ];

  // Fetch service notifications
  const fetchServiceNotifications = () => {
    setNotifLoading(true);
    setNotifError(null);

    MyYuukke.getServiceNotifications()
      .then((res) => {
        const list = res?.data?.data ?? [];
        setNotifications(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error("Failed to load notifications:", err);
        setNotifError("Failed to load notifications");
        setNotifications([]);
      })
      .finally(() => setNotifLoading(false));
  };

  useEffect(() => {
    setRunTour(true);
    dispatch(getAllCommonTagsList());
    dispatch(getAllCommonIndustry());
    dispatch(getAllCommonSchoolList());
    dispatch(getAllCommonFriendsList());
    dispatch(getAllAreaOfInterest());
    dispatch(getAllChatFriendsList());
    dispatch(getAllCommonProfessionList());
    dispatch(getAllCommonExpectationHelpList());
    dispatch(getAllCommonExpectationInterestList());
    dispatch(getApplicationRequirements());
    dispatch(getSubscriptionList());
    dispatch(setDashFocusTab(7));

    fetchServiceNotifications();
  }, [dispatch]);

  // Handle clicking outside of dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setMoreMenuOpen(false);
      }
      if (bellMenuRef.current && !bellMenuRef.current.contains(e.target)) {
        setBellMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    setIsToggleEnabled((prevState) => {
      const newState = !prevState;
      if (prevState === false && newState === true) {
        dispatch(setIsServiceUser(false));
        history.push("/default/home");
      }
      return newState;
    });
  };

  const handleProfileClick = () => {
    setProfileMenuOpen(false);
    dispatch(setDashFocusTab(0));
  };

  const handleLogoutClick = (e) => {
    setProfileMenuOpen(false);
    logout(e);
  };

  const handleBottomNavClick = (id) => {
    setMoreMenuOpen(false);
    dispatch(setDashFocusTab(id));
  };

  const handleMoreItemClick = (id) => {
    setMoreMenuOpen(false);
    dispatch(setDashFocusTab(id));
  };

  // Toggle bell dropdown and refresh notifications when opened
  const handleBellToggle = () => {
    setBellMenuOpen((prev) => {
      const next = !prev;
      if (next) fetchServiceNotifications();
      return next;
    });
  };

  // Navigate to the relevant tab based on the notification's type, then
  // mark it read locally and close the dropdown.
  const handleNotificationClick = (notification) => {
    const targetTab = NOTIFICATION_TYPE_TAB_MAP[notification.type] ?? DEFAULT_NOTIFICATION_TAB;

    dispatch(setDashFocusTab(targetTab));

    setNotifications((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
    );

    setBellMenuOpen(false);
  };

  // Clear all notifications. Clears the dropdown immediately and syncs the
  // clear with DELETE /api/v1/service-notifications.
  const handleClearAllNotifications = (e) => {
    e.stopPropagation();
    if (clearingNotifications || notifications.length === 0) return;

    setClearingNotifications(true);
    const previousNotifications = notifications;
    setNotifications([]);

    MyYuukke.clearServiceNotifications()
      .catch((err) => {
        console.error("Failed to clear notifications on the server:", err);
        // Restore so the user isn't left thinking notifications were cleared
        // when the request actually failed.
        setNotifications(previousNotifications);
      })
      .finally(() => setClearingNotifications(false));
  };

  // Clear a single notification via DELETE /api/v1/service-notifications/{id}.
  const handleClearOneNotification = (e, notification) => {
    e.stopPropagation();
    const previousNotifications = notifications;
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));

    MyYuukke.clearServiceNotification(notification.id).catch((err) => {
      console.error("Failed to clear notification on the server:", err);
      setNotifications(previousNotifications);
    });
  };

  const renderToggle = (extraClass) => (
    <div className={`toggleWrapper ${extraClass}`}>
      <span className={`toggleLabel ${!isToggleEnabled ? "toggleLabelActive" : ""}`}>
        Service
      </span>
      <button
        type="button"
        className={`toggleTrack toggle-button ${isToggleEnabled ? "toggleTrackOn" : ""}`}
        role="switch"
        aria-checked={isToggleEnabled}
        aria-label="Switch to Yuukke Community"
        onClick={handleToggle}
      >
        <span className={`toggleKnob ${isToggleEnabled ? "toggleKnobOn" : ""}`} />
      </button>
      <span className={`toggleLabel ${isToggleEnabled ? "toggleLabelActive" : ""}`}>
        Yuukke
      </span>
    </div>
  );

  const CustomTooltip = ({ step, tooltipProps }) => (
    <div
      {...tooltipProps}
      style={{
        backgroundColor: "#fff",
        padding: "16px",
        borderRadius: "8px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
        position: "relative",
      }}
    >
      <button
        onClick={() => setRunTour(false)}
        style={{
          position: "absolute",
          top: "5px",
          right: "8px",
          background: "none",
          border: "none",
          fontSize: "24px",
          cursor: "pointer",
          color: "#555",
          padding: "1px",
        }}
      >
        ×
      </button>
      <h4>{step.title}</h4>
      <p>{step.content}</p>
    </div>
  );

  const bottomNav = (
    <nav className="mobileBottomNav" ref={moreMenuRef}>
      {moreMenuOpen && (
        <div className="mobileMoreSheet">
          {moreTabList.map((tb, ind) => (
            <div
              key={ind}
              role="button"
              tabIndex={0}
              onClick={() => handleMoreItemClick(tb.id)}
              onKeyDown={(e) =>
                e.key === "Enter" && handleMoreItemClick(tb.id)
              }
              className={`mobileMoreSheetItem ${
                focusedTb === tb.id ? "mobileMoreSheetItemActive" : ""
              }`}
            >
              {tb.icon}
              <span>{tb.title}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mobileBottomNavRow">
        {bottomNavPrimary.map((tb) => (
          <button
            key={tb.id}
            type="button"
            className={`mobileBottomNavItem ${
              focusedTb === tb.id ? "mobileBottomNavItemActive" : ""
            }`}
            onClick={() => handleBottomNavClick(tb.id)}
          >
            {tb.icon}
            <span>{tb.label}</span>
          </button>
        ))}

        <button
          type="button"
          className={`mobileBottomNavItem ${
            isMoreActive || moreMenuOpen ? "mobileBottomNavItemActive" : ""
          }`}
          onClick={() => setMoreMenuOpen((prev) => !prev)}
          aria-haspopup="true"
          aria-expanded={moreMenuOpen}
        >
          <span className="mobileBottomNavMoreIconWrap">
            <FiMoreHorizontal size={BOTTOM_NAV_ICON_SIZE} />
          </span>
          <span>More</span>
        </button>
      </div>
    </nav>
  );

  return (
    <BlockUi
      tag="div"
      message="Loading, please wait"
      style={{ minHeight: "134px" }}
      className="dashBoardContainer"
      blocking={state.loader}
    >
      <ToastContainer position="top-right" autoClose={5000} />
      <Joyride
        steps={TOUR_STEPS}
        run={runTour}
        continuous={true}
        scrollToFirstStep={false}
        showSkipButton={false}
        showCloseButton={false}
        disableScrolling={true}
        tooltipComponent={CustomTooltip}
        styles={{
          options: { primaryColor: "#4CAF50", zIndex: 10000 },
          beacon: { display: "none" },
        }}
        locale={{ last: "Finish" }}
        scrollOffset={50}
      />

      <div className="dashboardHeader">
        <div className="dashboardHeader-row1">
          <div className="logoSection">
            <img
              src="https://corporate.yuukke.com/bcav2/auth/image/39bb22f2-52e9-43a3-951e-0618f8bc85fc"
              alt="Yuukke logo"
              className="logoImg"
            />
          </div>

          <nav className="tabSection">
            {resTabList.map((tb, ind) => (
              <div
                key={ind}
                role="button"
                tabIndex={0}
                onClick={() => dispatch(setDashFocusTab(tb.id))}
                onKeyDown={(e) =>
                  e.key === "Enter" && dispatch(setDashFocusTab(tb.id))
                }
                className={`tabItem ${focusedTb === tb.id ? "activeTab" : ""}`}
              >
                {tb.icon}
                <span>{tb.title}</span>
              </div>
            ))}
          </nav>

          <div className="headerRightActions">
            {renderToggle("headerToggleWrapper")}

            {/* Notifications Bell */}
            <div className="bellMenuWrapper" ref={bellMenuRef}>
              <button
                type="button"
                className="bellIconButton"
                onClick={handleBellToggle}
                aria-haspopup="true"
                aria-expanded={bellMenuOpen}
                aria-label="Open notifications"
              >
                <FiBell size={20} />
                {notifications.length > 0 && (
                  <span className="bellBadge">{notifications.length}</span>
                )}
              </button>

              {bellMenuOpen && (
                <div className="bellDropdown">
                  <div className="bellDropdownHeader">
                    <span>Notifications</span>
                    {notifications.length > 0 && (
                      <button
                        type="button"
                        className="bellDropdownClearAll"
                        onClick={handleClearAllNotifications}
                        disabled={clearingNotifications}
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className="bellDropdownContent">
                    {notifLoading ? (
                      <div className="bellDropdownItem">Loading...</div>
                    ) : notifError ? (
                      <div className="bellDropdownItem emptyState">
                        {notifError}
                      </div>
                    ) : notifications.length > 0 ? (
                      notifications.map((n, idx) => (
                        <div
                          className="bellDropdownItem"
                          key={n.id ?? idx}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleNotificationClick(n)}
                          onKeyDown={(e) =>
                            e.key === "Enter" && handleNotificationClick(n)
                          }
                        >
                          <span className="bellDropdownItemMessage">{n.message}</span>
                          <button
                            type="button"
                            className="bellDropdownItemClear"
                            aria-label="Clear notification"
                            onClick={(e) => handleClearOneNotification(e, n)}
                          >
                            ×
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="bellDropdownItem emptyState">
                        No new notifications
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Profile */}
            <div className="profileMenuWrapper profileMenuWrapper--right" ref={profileMenuRef}>
              <button
                type="button"
                className="profileIconButton profileIconButton--highlight"
                onClick={() => setProfileMenuOpen((prev) => !prev)}
                aria-haspopup="true"
                aria-expanded={profileMenuOpen}
                aria-label="Open profile menu"
              >
                <FiUser size={18} />
                {isProfileIncomplete && (
                  <span className="profileProgress profileProgress--corner">
                    {profileCompletion}%
                  </span>
                )}
              </button>

              {profileMenuOpen && (
                <div className="profileDropdown profileDropdown--right">
                  <div className="profileDropdownMobileHeader">
                    {renderToggle("dropdownToggleWrapper")}
                  </div>

                  <div className="profileDropdownItem" onClick={handleProfileClick}>
                    <FiUser size={ICON_SIZE} />
                    <span>Profile</span>
                  </div>
                  <div className="profileDropdownItem" onClick={handleLogoutClick}>
                    <FiLogOut size={ICON_SIZE} />
                    <span>Logout</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="dashBoardNavScreensConatiner">
        <CalendarContainerV2 from="service" />
      </div>

      {typeof document !== "undefined" &&
        ReactDOM.createPortal(bottomNav, document.body)}
    </BlockUi>
  );
};

export default withTranslation()(withRouter(ServiceUsersHome));